export interface ListItem {
  /**
   * Use this field to render something other than the plain string `value` for the contents of the list option.
   *
   * n.b. the `content` prop should not contain interactive elements, per the WAI-ARIA Listbox pattern described at:
   * https://www.w3.org/TR/wai-aria-practices-1.1/#Listbox
   */
  content?: React.ReactNode;
  /** A disabled item is shown with dimmed visual styling. Disabled items are focusable but not selectable. */
  disabled?: boolean;
  /**
   * When true, a new group of items will be defined, labeled by this item, and continuing until the next group or separator.
   * Group label items are neither selectable nor focusable.
   */
  group?: boolean;
  /** When true, this item will be rendered as a list separator, and all other properties of this interface will be ignored. */
  separator?: boolean;
  testId?: string;
  /** Tooltip shown on the list item. n.b. the implementation details of how the tooltip is shown are subject to change at any time. */
  tooltip?: string;
  /** This value is used for several purposes:
   *
   * * It is passed in the `ListProps.onSelect` callback.
   * * It is used as the text content of the rendered item if `content` is falsy.
   * * It is used for determining which list items are selected and/or focused.
   * * It is used by the default filtering comparator in `LocallyFilteringList`.
   *
   * Must be truthy unless `separator` is `true`. Must be unique within any array of `ListItem`.
   */
  value?: string;
}

/** Intended only for use for searching lists of items. Does not wrap, skips item at `fromIndex`,
 * and returns `fromIndex` if no match is found.  */
function findIndex<T>(items: T[], fn: (item: T) => boolean, fromIndex: number, direction: 1 | -1): number {
  for (let index = fromIndex + direction; index >= 0 && index < items.length; index += direction) {
    if (fn(items[index])) {
      return index;
    }
  }
  return fromIndex;
}

export function findFocusableItemIndex(items: ListItem[], fromIndex: number, direction: 1 | -1): number {
  return findIndex(items, isFocusableItem, fromIndex, direction);
}

export function findSelectableItemIndex(items: ListItem[], fromIndex: number, direction: 1 | -1): number {
  return findIndex(items, isSelectableItem, fromIndex, direction);
}

export function findIndexOfItemWithValue(items: ListItem[], value: string | undefined | null): number {
  if (!value) { return -1; }
  return findIndex(items, item => !item.group && item.value === value, -1, +1);
}

export function isFocusableItem(item: ListItem): boolean {
  return !!item && !!item.value && !item.separator && !item.group;
}

export function isSelectableItem(item: ListItem): boolean {
  return isFocusableItem(item) && !item.disabled;
}

/**
 * Returns true if `item` should be included in the list of items matching `filterValue`.
 */
export type ListItemFilterFn = (item: ListItem, filterValue: string) => boolean;
const caseInsensitiveValueMatch: ListItemFilterFn = (item, filterValue) =>
  isFocusableItem(item) && item.value!.toLowerCase().indexOf(filterValue.toLowerCase()) >= 0;

// tslint:disable-next-line:max-line-length
export function standardItemFilter(items: ListItem[], filterValue: string, filterFn: ListItemFilterFn = caseInsensitiveValueMatch): ListItem[] {
  return items.filter(item =>
    item.group || item.separator || filterFn(item, filterValue)
  ).filter((item, index, filteredItems) => // filter out consecutive separators
    !(item.separator && index > 0 && filteredItems[index - 1].separator)
  ).filter((item, index, filteredItems) => // filter out list-initial and list-terminal separators
    !(item.separator && (index === 0 || index === filteredItems.length - 1))
  );
}
